(function () {
    'use strict';

    const ACCOUNT_URL = '/app/account';
    const PHONE_PATTERN = /^\+[1-9]\d{7,14}$/;
    const CODE_PATTERN = /^\d{6}$/;
    const RESEND_COOLDOWN_SECONDS = 30;

    function ready(fn) {
        if (document.readyState !== 'loading') {
            fn();
            return;
        }

        document.addEventListener('DOMContentLoaded', fn);
    }

    function parseConfig(root) {
        try {
            return JSON.parse(root.getAttribute('data-firebase-config') || '{}');
        } catch (e) {
            return {};
        }
    }

    function createBody(values) {
        const body = new FormData();
        Object.entries(values).forEach(([key, value]) => {
            body.append(key, String(value));
        });

        return body;
    }

    async function post(endpoint, values) {
        if (!window.ApiClient) {
            throw new Error('api_client_missing');
        }

        return window.ApiClient.request(endpoint, {
            method: 'POST',
            body: createBody(values),
        });
    }

    async function responseJson(response) {
        try {
            return await response.json();
        } catch (e) {
            return {};
        }
    }

    function setHidden(element, hidden) {
        if (element) {
            element.classList.toggle('hidden', hidden);
        }
    }

    function setBusy(button, spinnerId, busy) {
        if (button) {
            button.disabled = busy;
        }

        setHidden(document.getElementById(spinnerId), !busy);
    }

    ready(function () {
        const root = document.getElementById('verify-phone-root');
        if (!root) return;

        window.LucideIcons?.initialize(root);

        const config = parseConfig(root);
        const redirectUrl = root.getAttribute('data-redirect') || ACCOUNT_URL;
        const phoneForm = document.getElementById('verify-phone-phone-form');
        const codeForm = document.getElementById('verify-phone-code-form');
        const phoneInput = document.getElementById('verify-phone-number');
        const codeInput = document.getElementById('verify-phone-code');
        const sendButton = document.getElementById('verify-phone-send');
        const confirmButton = document.getElementById('verify-phone-confirm');
        const resendButton = document.getElementById('verify-phone-resend');
        const changeNumberButton = document.getElementById('verify-phone-change-number');
        const resendLabel = document.getElementById('verify-phone-resend-label');
        const resendDefaultLabel = resendLabel ? resendLabel.textContent : '';
        const sendSpinnerId = 'verify-phone-send-spinner';
        const confirmSpinnerId = 'verify-phone-confirm-spinner';
        const resendSpinnerId = 'verify-phone-resend-spinner';
        const errorBox = document.getElementById('verify-phone-error');
        const unavailableBox = document.getElementById('verify-phone-unavailable');
        const successBox = document.getElementById('verify-phone-success');

        if (!phoneForm || !codeForm || !phoneInput || !codeInput) return;

        const state = {
            auth: null,
            confirmationResult: null,
            nonce: '',
            recaptchaVerifier: null,
            recaptchaWidgetId: null,
            sending: false,
            confirming: false,
            cooldownTimer: null,
            cooldownRemaining: 0,
        };

        function setResendBusy(busy) {
            // While a resend cooldown counts down, the button stays disabled, so a
            // finishing send (busy=false) must not re-enable it. Only the cooldown
            // ending re-enables it.
            if (resendButton && (busy || state.cooldownRemaining <= 0)) {
                resendButton.disabled = busy;
            }

            const resendSpinner = document.getElementById(resendSpinnerId);
            if (resendSpinner) {
                resendSpinner.classList.toggle('animate-spin', busy);
            }
        }

        function resendCooldownLabel(seconds) {
            if (window.trans) {
                return window.trans('auth', 'phone_verify_resend_in', { seconds: seconds });
            }

            return 'Resend in ' + seconds + 's';
        }

        function stopResendCooldown() {
            if (state.cooldownTimer) {
                window.clearInterval(state.cooldownTimer);
                state.cooldownTimer = null;
            }

            state.cooldownRemaining = 0;
            if (resendLabel) {
                resendLabel.textContent = resendDefaultLabel;
            }
            if (resendButton) {
                resendButton.disabled = false;
            }
        }

        function tickResendCooldown() {
            state.cooldownRemaining -= 1;
            if (state.cooldownRemaining <= 0) {
                stopResendCooldown();
                return;
            }

            if (resendLabel) {
                resendLabel.textContent = resendCooldownLabel(state.cooldownRemaining);
            }
        }

        function startResendCooldown() {
            stopResendCooldown();
            state.cooldownRemaining = RESEND_COOLDOWN_SECONDS;
            if (resendButton) {
                resendButton.disabled = true;
            }
            if (resendLabel) {
                resendLabel.textContent = resendCooldownLabel(state.cooldownRemaining);
            }
            state.cooldownTimer = window.setInterval(tickResendCooldown, 1000);
        }

        function changeNumber() {
            // Return to the phone step. Drop any pending confirmation and nonce so a
            // stale Firebase session can't confirm against the previous number.
            state.confirmationResult = null;
            state.nonce = '';
            stopResendCooldown();
            hideMessages();
            codeInput.value = '';
            setHidden(codeForm, true);
            setHidden(phoneForm, false);
            phoneInput.focus();
        }

        function hideMessages() {
            setHidden(errorBox, true);
            setHidden(unavailableBox, true);
            setHidden(successBox, true);
        }

        function showError() {
            hideMessages();
            setHidden(errorBox, false);
        }

        function showUnavailable() {
            hideMessages();
            setHidden(unavailableBox, false);
        }

        function showSuccess() {
            hideMessages();
            setHidden(successBox, false);
        }

        function showCodeForm() {
            setHidden(phoneForm, true);
            setHidden(codeForm, false);
            codeInput.focus();
        }

        async function ensureFirebase() {
            const firebase = window.firebase;
            if (!firebase || !firebase.auth) {
                throw new Error('firebase_missing');
            }

            if (!firebase.apps || firebase.apps.length === 0) {
                firebase.initializeApp(config);
            }

            const auth = firebase.auth();
            await auth.setPersistence(firebase.auth.Auth.Persistence.SESSION);
            state.auth = auth;

            if (!state.recaptchaVerifier) {
                state.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
                    'recaptcha-container',
                    { size: 'invisible' }
                );
                state.recaptchaWidgetId = await state.recaptchaVerifier.render();
            }

            return auth;
        }

        function resetRecaptcha() {
            if (
                state.recaptchaWidgetId !== null
                && window.grecaptcha
                && typeof window.grecaptcha.reset === 'function'
            ) {
                window.grecaptcha.reset(state.recaptchaWidgetId);
            }
        }

        async function requestSession(phone) {
            const response = await post('/phone-verification/session', {
                phone_e164: phone,
            });
            const data = await responseJson(response);

            if (!response.ok) {
                throw new Error('session_failed');
            }

            if (data.sendable !== true) {
                showUnavailable();
                return null;
            }

            if (!data.nonce) {
                throw new Error('nonce_missing');
            }

            return data;
        }

        async function sendCode() {
            if (state.sending) return;

            const phone = phoneInput.value.trim();
            hideMessages();

            if (!PHONE_PATTERN.test(phone)) {
                showError();
                phoneInput.focus();
                return;
            }

            state.sending = true;
            setBusy(sendButton, sendSpinnerId, true);
            setResendBusy(true);

            try {
                const session = await requestSession(phone);
                if (!session) return;

                state.nonce = String(session.nonce);
                const auth = await ensureFirebase();
                state.confirmationResult = await auth.signInWithPhoneNumber(phone, state.recaptchaVerifier);
                showCodeForm();
                startResendCooldown();
            } catch (e) {
                resetRecaptcha();
                showError();
                if (window.console) {
                    window.console.warn('verify-phone:', e);
                }
            } finally {
                state.sending = false;
                setBusy(sendButton, sendSpinnerId, false);
                setResendBusy(false);
            }
        }

        async function confirmCode() {
            if (state.confirming) return;

            const code = codeInput.value.replace(/\s+/g, '');
            hideMessages();

            if (!CODE_PATTERN.test(code) || !state.confirmationResult || !state.nonce) {
                showError();
                codeInput.focus();
                return;
            }

            state.confirming = true;
            setBusy(confirmButton, confirmSpinnerId, true);

            try {
                const credential = await state.confirmationResult.confirm(code);
                let token = '';
                try {
                    token = await credential.user.getIdToken();
                } finally {
                    if (state.auth) {
                        try {
                            await state.auth.signOut();
                        } catch (signOutError) {
                            if (window.console) {
                                window.console.warn('verify-phone signout:', signOutError);
                            }
                        }
                    }
                }

                const response = await post('/phone-verification/confirm', {
                    firebase_id_token: token,
                    nonce: state.nonce,
                });
                const data = await responseJson(response);

                if (!response.ok || data.status !== 'verified') {
                    showError();
                    return;
                }

                showSuccess();
                window.location.assign(redirectUrl || ACCOUNT_URL);
            } catch (e) {
                showError();
                if (window.console) {
                    window.console.warn('verify-phone:', e);
                }
            } finally {
                state.confirming = false;
                setBusy(confirmButton, confirmSpinnerId, false);
            }
        }

        phoneForm.addEventListener('submit', function (event) {
            event.preventDefault();
            sendCode();
        });

        codeForm.addEventListener('submit', function (event) {
            event.preventDefault();
            confirmCode();
        });

        if (resendButton) {
            resendButton.addEventListener('click', function () {
                codeInput.value = '';
                sendCode();
            });
        }

        if (changeNumberButton) {
            changeNumberButton.addEventListener('click', function () {
                changeNumber();
            });
        }
    });
})();
